<template>
  <div class="app-surface" @click="openMenuId = null">
    <NavBar />
    
    <main class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <div class="mb-8 rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-soft md:p-6">
        <div class="flex justify-between items-center">
          <div>
            <div class="mb-2 inline-flex rounded-lg bg-jackfruit-100 px-3 py-1.5 text-xs font-black text-ink-700">私人追更区</div>
            <h1 class="text-3xl font-black text-ink-900">今天继续哪本？</h1>
            <p class="mt-2 text-sm text-ink-500">收藏你喜欢的小说，随时接着看下一章。</p>
          </div>
          <div class="rounded-xl bg-jackfruit-50 px-4 py-3 text-sm font-black text-ink-700">
            共 {{ books.length }} 本
          </div>
        </div>
      </div>

      <!-- 加载 -->
      <div v-if="isLoading" class="flex justify-center py-16">
        <div class="h-10 w-10 animate-spin rounded-full border-b-2 border-jackfruit-500"></div>
      </div>

      <!-- 空书架 -->
      <div v-else-if="books.length === 0" class="text-center py-16">
        <div class="text-6xl mb-4">📭</div>
        <h3 class="mb-2 text-lg font-black text-ink-900">书架空空如也</h3>
        <p class="mb-6 text-sm text-ink-500">快去发现喜欢的小说，加入书架吧！</p>
        <router-link to="/books" class="inline-flex items-center rounded-xl bg-jackfruit-500 px-5 py-2.5 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600">
          去发现小说
        </router-link>
      </div>

      <!-- 书架网格 -->
      <div v-else class="grid grid-cols-3 gap-2.5 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
        <div 
          v-for="book in books" 
          :key="book.id"
          class="group relative overflow-visible transition-all hover:-translate-y-1"
          @click.stop
        >
          <!-- 封面区 -->
          <router-link
            :to="`/reading/${book.id}/${book.lastChapterNumber || 1}?from=shelf`"
            class="relative block aspect-[3/4] overflow-hidden rounded-lg shadow-soft"
            :class="coverBg(book.category)"
            @touchstart.passive="startLongPress(book)"
            @touchend.passive="cancelLongPress"
            @touchcancel.passive="cancelLongPress"
            @mousedown.left="startLongPress(book)"
            @mouseup="cancelLongPress"
            @mouseleave="cancelLongPress"
            @contextmenu.prevent="openBookMenu(book)"
          >
            <img
              v-if="book.coverUrl && !book.coverUrl.includes('example.com')"
              :src="book.coverUrl"
              :alt="book.title"
              class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              @error="(e) => { (e.target as HTMLImageElement).style.display = 'none' }"
            />
            <div v-else class="absolute inset-0 flex items-center justify-center">
              <span class="text-3xl sm:text-5xl">📖</span>
            </div>
            <!-- 阅读进度 -->
            <div v-if="book.readingProgress > 0" class="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/50 to-transparent">
              <div class="text-white text-[10px] mb-1 flex justify-between">
                <span>已读 {{ Math.round(book.readingProgress) }}%</span>
                <span class="font-bold text-jackfruit-300">
                  继续
                </span>
              </div>
              <div class="w-full bg-white/30 rounded-full h-1">
                <div 
                  class="h-1 rounded-full bg-jackfruit-pop transition-all"
                  :style="{ width: book.readingProgress + '%' }"
                ></div>
              </div>
            </div>
          </router-link>
          
          <!-- 信息 -->
          <div class="pt-1.5">
            <router-link :to="`/reading/${book.id}/${book.lastChapterNumber || 1}?from=shelf`">
              <h3 class="truncate text-xs font-black text-ink-900 transition-colors group-hover:text-pulp-600 sm:text-sm">{{ book.title }}</h3>
            </router-link>
          </div>

          <div
            v-if="openMenuId === book.id"
            class="absolute right-0 top-2 z-50 w-44 overflow-hidden rounded-xl border border-ink-100 bg-white py-1 text-sm shadow-fruit"
            @click.stop
          >
            <button class="bookshelf-menu-item" @click.stop="shareBook(book)">分享</button>
            <button class="bookshelf-menu-item" @click.stop="pinBook(book)">置顶</button>
            <button class="bookshelf-menu-item" @click.stop="categorizeBook(book)">分类</button>
            <button
              class="bookshelf-menu-item disabled:text-ink-300"
              :disabled="!canPublishFromShelf(book)"
              @click.stop="publishFromShelf(book)"
            >
              以创作者身份上架
            </button>
            <button class="bookshelf-menu-item text-red-500" @click.stop="confirmRemove(book)">从书架移除</button>
          </div>
        </div>
      </div>

    </main>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import NavBar from '@/components/NavBar.vue'
import { userApi } from '@/api'
import { SUBCATEGORIES } from '@/config/categories'

interface BookWithProgress {
  id: number
  title: string
  author: string
  coverUrl: string
  category: string
  tags?: string[] | string
  totalChapters: number
  readingProgress: number
  lastChapterNumber?: number
  visibility?: 'public' | 'private'
  totalChoiceCount?: number
  customChoiceCount?: number
  originType?: string
}

const books = ref<BookWithProgress[]>([])
const isLoading = ref(true)
const isRemoving = ref<number | null>(null)
const openMenuId = ref<number | null>(null)
let longPressTimer: number | null = null

const coverBg = (category: string) => {
  const map: Record<string, string> = {
    male: 'bg-gradient-to-br from-sky-100 to-blue-50',
    female: 'bg-gradient-to-br from-rose-100 to-pink-50',
    general: 'bg-gradient-to-br from-amber-100 to-orange-50'
  }
  return map[category] || 'bg-gradient-to-br from-gray-100 to-gray-50'
}

const normalizedTags = (book: BookWithProgress) => {
  const raw = book.tags
  if (Array.isArray(raw)) return raw
  if (typeof raw !== 'string') return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return raw.split(',').map(tag => tag.trim()).filter(Boolean)
  }
}

const subcategoryLabel = (book: BookWithProgress) => {
  const tags = normalizedTags(book)
  const matched = SUBCATEGORIES.find(sub =>
    tags.includes(sub.subcategoryTag) ||
    tags.includes(sub.id) ||
    tags.includes(sub.label)
  )
  return matched?.label || ''
}

const openBookMenu = (book: BookWithProgress) => {
  openMenuId.value = book.id
}

const cancelLongPress = () => {
  if (longPressTimer !== null) {
    window.clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

const startLongPress = (book: BookWithProgress) => {
  cancelLongPress()
  longPressTimer = window.setTimeout(() => {
    openBookMenu(book)
    longPressTimer = null
  }, 520)
}

const canPublishFromShelf = (book: BookWithProgress) => {
  if (book.visibility !== 'private') return false
  if (book.originType === 'original') return book.totalChapters >= 5
  const total = book.totalChoiceCount || 0
  const custom = book.customChoiceCount || 0
  return book.totalChapters >= 20 && total >= 20 && custom / Math.max(total, 1) >= 0.5
}

const shareBook = (book: BookWithProgress) => {
  openMenuId.value = null
  navigator.clipboard?.writeText(`${book.title}：${window.location.origin}/reading/${book.id}/${book.lastChapterNumber || 1}`)
  alert('分享链接已复制')
}

const pinBook = (_book: BookWithProgress) => {
  openMenuId.value = null
  alert('置顶功能会在书架分组一起完善')
}

const categorizeBook = (_book: BookWithProgress) => {
  openMenuId.value = null
  alert('分类功能会和书架分组一起开放')
}

const publishFromShelf = (book: BookWithProgress) => {
  if (!canPublishFromShelf(book)) {
    alert('还没满足上架条件：原创书需满 5 章；二创分支需新增 20 章且自定义方向占比达标。')
    return
  }
  openMenuId.value = null
  alert('请进入阅读页文末的创作者入口提交上架')
}

const confirmRemove = async (book: BookWithProgress) => {
  openMenuId.value = null
  const confirmed = window.confirm(`确定把《${book.title}》从书架移除吗？阅读记录不会公开展示，但书架入口会消失。`)
  if (!confirmed) return
  await removeFromBookshelf(book.id)
}

const loadBookshelf = async () => {
  isLoading.value = true
  try {
    const res = await userApi.getBookshelf({ page: 1, limit: 100 })
    if (res.code === 200 && res.data) {
      books.value = (res.data.items || []).map(book => ({
        ...book,
        tags: normalizedTags(book as BookWithProgress)
      }))
    }
  } catch {
    // ignore
  } finally {
    isLoading.value = false
  }
}

const removeFromBookshelf = async (bookId: number) => {
  isRemoving.value = bookId
  try {
    await userApi.removeFromBookshelf(bookId)
    books.value = books.value.filter(b => b.id !== bookId)
  } catch {
    // ignore
  } finally {
    isRemoving.value = null
  }
}

onMounted(loadBookshelf)
</script>

<style scoped>
.bookshelf-menu-item {
  display: block;
  width: 100%;
  padding: 0.65rem 0.85rem;
  text-align: left;
  font-weight: 800;
  color: #4d4438;
  transition: background 0.16s ease;
}

.bookshelf-menu-item:hover:not(:disabled) {
  background: #fff7d7;
}

.bookshelf-menu-item:disabled {
  cursor: not-allowed;
}
</style>
